import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

interface SetupFormData {
  username: string;
  password: string;
  confirmPassword: string;
  email: string;
  serverName: string;
  serverDescription: string;
  welcomeMessage: string;
  serverRules: string;
}

function SetupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Check if setup is already complete and redirect if so
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await api.get('/setup/status');
        if (!response.data.setupRequired) {
          // Setup is already complete, redirect to login
          console.log('Setup already complete, redirecting to login...');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking setup status:', error);
        // If we can't check, let the user continue with setup
      }
    };

    checkSetupStatus();
  }, []);
  const [formData, setFormData] = useState<SetupFormData>({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    serverName: '',
    serverDescription: '',
    welcomeMessage: '',
    serverRules: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNext = () => {
    setError('');

    if (step === 1) {
      // Validate owner account
      if (!formData.username || formData.username.length < 3) {
        setError('Username must be at least 3 characters');
        return;
      }
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        setError('A valid email address is required');
        return;
      }
      if (!formData.password || formData.password.length < 12) {
        setError('Password must be at least 12 characters');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      // Check password strength
      const hasUppercase = /[A-Z]/.test(formData.password);
      const hasLowercase = /[a-z]/.test(formData.password);
      const hasNumber = /\d/.test(formData.password);
      const hasSpecial = /[@$!%*?&]/.test(formData.password);
      if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
        setError('Password must contain uppercase, lowercase, number, and special character');
        return;
      }
    }

    if (step === 2) {
      // Validate server config
      if (!formData.serverName || formData.serverName.length < 1) {
        setError('Server name is required');
        return;
      }
    }

    setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleComplete = async () => {
    setError('');
    setLoading(true);

    try {
      // Create owner account
      await api.post('/setup/owner-account', {
        username: formData.username.trim(),
        password: formData.password,
        email: formData.email.trim(),
      });

      // Configure server
      await api.post('/setup/server-config', {
        serverName: formData.serverName,
        serverDescription: formData.serverDescription || undefined,
      });

      // Set server rules
      if (formData.welcomeMessage || formData.serverRules) {
        await api.post('/setup/server-rules', {
          welcomeMessage: formData.welcomeMessage || undefined,
          serverRules: formData.serverRules || undefined,
        });
      }

      // Complete setup
      await api.post('/setup/complete');

      // Small delay to ensure backend has processed the complete request
      await new Promise((resolve) => setTimeout(resolve, 500));

      console.log('Setup completed successfully, redirecting to login...');
      // Redirect to login
      navigate('/login');
    } catch (err: any) {
      console.error('Setup error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      const errorMsg =
        err.response?.data?.error || err.response?.data?.message || err.message || 'Setup failed';
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-background dark:bg-brand-background px-4">
      <div className="card max-w-2xl w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-center mb-2">Setup Wizard</h1>
          <p className="text-center text-brand-on-surface-variant dark:text-brand-on-surface-variant">
            Step {step} of 4
          </p>
          <div className="mt-4 flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`flex-1 h-2 rounded ${
                  s <= step
                    ? 'bg-primary-600'
                    : 'bg-brand-surface-variant dark:bg-brand-surface-variant'
                }`}
              />
            ))}
          </div>
        </div>

        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>}

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Create Owner Account</h2>
            <div>
              <label className="block text-sm font-medium mb-2">
                Username <span className="text-brand-error">*</span>
              </label>
              <input
                type="text"
                className="input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter username (min 3 characters)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Email <span className="text-brand-error">*</span>
              </label>
              <input
                type="email"
                className="input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Password <span className="text-brand-error">*</span>
              </label>
              <input
                type="password"
                className="input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 12 chars, uppercase, lowercase, number, special"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Confirm Password <span className="text-brand-error">*</span>
              </label>
              <input
                type="password"
                className="input"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Re-enter password"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Server Configuration</h2>
            <div>
              <label className="block text-sm font-medium mb-2">
                Server Name <span className="text-brand-error">*</span>
              </label>
              <input
                type="text"
                className="input"
                value={formData.serverName}
                onChange={(e) => setFormData({ ...formData, serverName: e.target.value })}
                placeholder="My Chat Server"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Server Description (optional)
              </label>
              <textarea
                className="input"
                rows={3}
                value={formData.serverDescription}
                onChange={(e) => setFormData({ ...formData, serverDescription: e.target.value })}
                placeholder="A brief description of your server"
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Welcome & Rules (Optional)</h2>
            <div>
              <label className="block text-sm font-medium mb-2">Welcome Message</label>
              <textarea
                className="input"
                rows={3}
                value={formData.welcomeMessage}
                onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                placeholder="Welcome to our server!"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Server Rules</label>
              <textarea
                className="input"
                rows={5}
                value={formData.serverRules}
                onChange={(e) => setFormData({ ...formData, serverRules: e.target.value })}
                placeholder="1. Be respectful&#10;2. No spam&#10;3. Have fun!"
              />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4">Review & Complete</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                  Owner Username
                </p>
                <p className="font-semibold">{formData.username}</p>
              </div>
              {formData.email && (
                <div>
                  <p className="text-sm text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                    Email
                  </p>
                  <p className="font-semibold">{formData.email}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                  Server Name
                </p>
                <p className="font-semibold">{formData.serverName}</p>
              </div>
              {formData.serverDescription && (
                <div>
                  <p className="text-sm text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                    Server Description
                  </p>
                  <p className="font-semibold">{formData.serverDescription}</p>
                </div>
              )}
            </div>
            <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900 rounded-lg">
              <p className="text-sm">
                Clicking "Complete Setup" will create your owner account, configure the server, and
                create default channels. You'll be redirected to login.
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-4">
          {step > 1 && (
            <button onClick={handleBack} className="btn btn-secondary flex-1" disabled={loading}>
              Back
            </button>
          )}
          {step < 4 ? (
            <button onClick={handleNext} className="btn btn-primary flex-1">
              Next
            </button>
          ) : (
            <button onClick={handleComplete} className="btn btn-primary flex-1" disabled={loading}>
              {loading ? 'Setting up...' : 'Complete Setup'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SetupWizard;
